import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  orderBy,
  where,
  Timestamp,
  DocumentReference,
} from "firebase/firestore";
import { db } from "./init";
import { Drama, VideoMeta } from "@/types/video";
import { buildPublicStorageUrl } from "@/utils/storageUtils";

/**
 * 獲取所有劇集
 */
export async function getDramas(): Promise<Drama[]> {
  try {
    const dramasRef = collection(db, "dramas");
    const q = query(dramasRef, orderBy("created_at", "desc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertDramaData(doc.data()),
    })) as Drama[];
  } catch (error) {
    console.error("Error getting dramas:", error);
    throw error;
  }
}

/**
 * 獲取單個劇集
 */
export async function getDrama(id: string): Promise<Drama | null> {
  try {
    const dramaRef = doc(db, "dramas", id);
    const dramaSnap = await getDoc(dramaRef);

    if (dramaSnap.exists()) {
      return {
        id: dramaSnap.id,
        ...convertDramaData(dramaSnap.data()),
      } as Drama;
    }

    return null;
  } catch (error) {
    console.error("Error getting drama:", error);
    throw error;
  }
}

/**
 * 取得指定製作公司旗下的所有劇集
 * @param companyDocId companies/{id} 的 Firestore 文件 ID
 */
export async function getDramasByCompany(companyDocId: string): Promise<Drama[]> {
  try {
    const dramasRef = collection(db, "dramas");
    const companyRef = doc(db, "companies", companyDocId);
    const q = query(dramasRef, where("company", "==", companyRef));
    const querySnapshot = await getDocs(q);

    const dramas = querySnapshot.docs.map((d) => ({
      id: d.id,
      ...convertDramaData(d.data()),
    })) as Drama[];

    // 客戶端排序：created_at desc（避免需要額外 composite index）
    dramas.sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });

    return dramas;
  } catch (error) {
    console.error("Error getting dramas by company:", error);
    throw error;
  }
}

/**
 * 取得卡司（actors 陣列）包含指定演員的所有劇集（含未上架）。
 * 只用 array-contains 單欄位查詢，不需要 composite index；排序在客戶端做。
 * @param actorId actors/{id} 的 Firestore 文件 ID
 */
export async function getDramasByActor(actorId: string): Promise<Drama[]> {
  try {
    const dramasRef = collection(db, "dramas");
    const actorRef = doc(db, "actors", actorId);
    const q = query(dramasRef, where("actors", "array-contains", actorRef));
    const querySnapshot = await getDocs(q);

    const dramas = querySnapshot.docs.map((d) => ({
      id: d.id,
      ...convertDramaData(d.data()),
    })) as Drama[];

    // 已上架在前，再依 created_at desc
    dramas.sort((a, b) => {
      if (!!a.isReleased !== !!b.isReleased) return a.isReleased ? -1 : 1;
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });

    return dramas;
  } catch (error) {
    console.error("Error getting dramas by actor:", error);
    throw error;
  }
}

/**
 * 創建劇集
 */
export async function createDrama(data: Omit<Drama, "id" | "createdAt" | "updatedAt" | "created_at" | "updated_at">): Promise<string> {
  try {
    // 🔑 doc id 必須等於 drama_id 欄位（unlockVideo 等下游 API 用 video.drama_id 直接 db.doc(`dramas/${drama_id}`) 查）
    const dramaId = typeof data.drama_id === "string" && data.drama_id.trim() !== ""
      ? data.drama_id.trim()
      : `drama_${Date.now()}`;

    const dramaRef = doc(db, "dramas", dramaId);

    // 防呆：若已有同 id 的劇集存在，直接擋下，不要 overwrite
    const existing = await getDoc(dramaRef);
    if (existing.exists()) {
      throw new Error(`createDrama: 劇集 ID "${dramaId}" 已存在，請改用其他 ID`);
    }

    // 轉換參考欄位為 DocumentReference
    const dramaData: any = {
      ...data,
      drama_id: dramaId,
      drama_play_times: data.drama_play_times || 0,
      drama_length: data.drama_length || 0,
      created_at: Timestamp.now(),
      updated_at: Timestamp.now(),
    };

    // 處理 episode_unlock_coin_cost：只有有效正數才寫入，避免寫入 null/undefined
    if (
      data.episode_unlock_coin_cost === null ||
      data.episode_unlock_coin_cost === undefined ||
      data.episode_unlock_coin_cost <= 0
    ) {
      delete dramaData.episode_unlock_coin_cost;
    }

    // 全面撤除 IAP 全劇購買：新建劇集時不帶入任何 legacy 欄位（防呆，type 已擋）
    delete dramaData.enable_full_drama_purchase;
    delete dramaData.full_drama_product_id;
    delete dramaData.full_drama_price_display;

    // 轉換 tags, actors, related_products 陣列為 DocumentReference（濾除 undefined / 空字串）
    const isValidId = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";

    if (Array.isArray(data.tags)) {
      const validTags = data.tags.filter(isValidId);
      if (validTags.length > 0) {
        dramaData.tags = validTags.map((tagId) => doc(db, "tags", tagId));
      } else {
        delete dramaData.tags;
      }
    }
    if (Array.isArray(data.actors)) {
      const validActors = data.actors.filter(isValidId);
      if (validActors.length > 0) {
        dramaData.actors = validActors.map((actorId) => doc(db, "actors", actorId));
      } else {
        delete dramaData.actors;
      }
    }
    if (Array.isArray(data.related_products)) {
      const validProducts = data.related_products.filter(isValidId);
      if (validProducts.length > 0) {
        dramaData.related_products = validProducts.map((productId) => doc(db, "products", productId));
      } else {
        delete dramaData.related_products;
      }
    }
    if (isValidId(data.company)) {
      dramaData.company = doc(db, "companies", data.company);
    } else {
      delete dramaData.company;
    }

    await setDoc(dramaRef, dramaData);
    return dramaId;
  } catch (error) {
    console.error("Error creating drama:", error);
    throw error;
  }
}

/**
 * 更新劇集
 */
export async function updateDrama(id: string, data: Partial<Drama>): Promise<void> {
  try {
    if (typeof id !== "string" || id.trim() === "") {
      throw new Error(`updateDrama: 缺少有效的劇集 ID（收到：${JSON.stringify(id)}）`);
    }
    const dramaRef = doc(db, "dramas", id);
    const updateData: any = {
      ...data,
    };

    // 移除不需要更新的欄位
    delete updateData.id;
    delete updateData.createdAt;
    delete updateData.updatedAt;
    delete updateData.created_at;
    delete updateData.updated_at;
    updateData.updated_at = Timestamp.now();

    // 轉換參考欄位為 DocumentReference（過濾 undefined / 空字串避免 Firestore path 驗證爆掉）
    const isValidId = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";

    if (data.tags !== undefined) {
      const validTags = Array.isArray(data.tags) ? data.tags.filter(isValidId) : [];
      updateData.tags = validTags.length > 0
        ? validTags.map((tagId) => doc(db, "tags", tagId))
        : [];
    }
    if (data.actors !== undefined) {
      const validActors = Array.isArray(data.actors) ? data.actors.filter(isValidId) : [];
      updateData.actors = validActors.length > 0
        ? validActors.map((actorId) => doc(db, "actors", actorId))
        : [];
    }
    if (data.related_products !== undefined) {
      const validProducts = Array.isArray(data.related_products) ? data.related_products.filter(isValidId) : [];
      updateData.related_products = validProducts.length > 0
        ? validProducts.map((productId) => doc(db, "products", productId))
        : [];
    }
    if (data.company !== undefined) {
      if (isValidId(data.company)) {
        updateData.company = doc(db, "companies", data.company);
      } else {
        updateData.company = null;
      }
    }

    // 處理 episode_unlock_coin_cost：null/undefined/0 → deleteField()，有效正數 → 寫入
    if ("episode_unlock_coin_cost" in data) {
      const cost = data.episode_unlock_coin_cost;
      if (cost === null || cost === undefined || cost <= 0) {
        // 清除欄位（回到預設 30）
        updateData.episode_unlock_coin_cost = deleteField();
      } else {
        // 寫入有效數值
        updateData.episode_unlock_coin_cost = cost;
      }
    }

    // 處理 scheduled_publish_at：Date → Timestamp，null → deleteField()（取消預約）
    if ("scheduled_publish_at" in data) {
      const at = data.scheduled_publish_at;
      updateData.scheduled_publish_at = at instanceof Date ? Timestamp.fromDate(at) : deleteField();
    }

    // 處理 paid_coin_rate：null/undefined → deleteField()（未設定，payout 0），>= 0 → 寫入
    if ("paid_coin_rate" in data) {
      const rate = data.paid_coin_rate;
      if (rate === null || rate === undefined || typeof rate !== "number" || rate < 0) {
        updateData.paid_coin_rate = deleteField();
      } else {
        updateData.paid_coin_rate = rate;
      }
    }

    // 全面撤除 IAP 全劇購買：任何 drama 被更新時一律清理 legacy 欄位
    updateData.enable_full_drama_purchase = deleteField();
    updateData.full_drama_product_id = deleteField();
    updateData.full_drama_price_display = deleteField();

    await updateDoc(dramaRef, updateData);
  } catch (error) {
    console.error("Error updating drama:", error);
    throw error;
  }
}

/**
 * 刪除劇集
 */
export async function deleteDrama(id: string): Promise<void> {
  try {
    const dramaRef = doc(db, "dramas", id);
    await deleteDoc(dramaRef);
  } catch (error) {
    console.error("Error deleting drama:", error);
    throw error;
  }
}

/**
 * 同步建構劇集封面圖片 URL
 *
 * drama_covers bucket 為公開可讀,直接組 CDN URL,瀏覽器自動 disk-cache。
 */
export function getDramaCoverUrl(
  coverUrl: string | undefined,
  defaultPrefix: string = "drama_covers"
): string {
  return buildPublicStorageUrl(coverUrl, defaultPrefix);
}

/**
 * 轉換劇集數據（處理 Timestamp 和 DocumentReference）
 */
function convertDramaData(data: any): any {
  const converted: any = {};

  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const value = data[key];

      // 處理 Timestamp
      if (value && typeof value === "object" && "toDate" in value) {
        converted[key] = value.toDate();
      }
      // 處理 DocumentReference（轉換為 ID）
      else if (value && typeof value === "object" && "id" in value && "path" in value) {
        converted[key] = value.id;
      }
      // 處理陣列中的 DocumentReference（特別處理 episodes 欄位）
      else if (Array.isArray(value)) {
        converted[key] = value.map((item) => {
          if (item && typeof item === "object" && "id" in item && "path" in item) {
            return item.id;
          }
          if (item && typeof item === "object" && "toDate" in item) {
            return item.toDate();
          }
          return item;
        });
      } else {
        converted[key] = value;
      }
    }
  }

  return converted;
}

/**
 * 獲取劇集及其所有影集數據
 */
export async function getDramaWithEpisodes(id: string): Promise<{
  drama: Drama | null;
  videos: (VideoMeta & { id: string })[];
}> {
  try {
    // 直接從 Firestore 讀取，獲取原始的 DocumentReference
    const dramaRef = doc(db, "dramas", id);
    const dramaSnap = await getDoc(dramaRef);

    if (!dramaSnap.exists()) {
      return { drama: null, videos: [] };
    }

    const dramaData = dramaSnap.data();
    const drama = {
      id: dramaSnap.id,
      ...convertDramaData(dramaData),
    } as Drama;

    // 優先使用 episodes 欄位（DocumentReference 陣列）
    let videos: (VideoMeta & { id: string })[] = [];
    
    const episodesRefs = dramaData.episodes;
    if (episodesRefs && Array.isArray(episodesRefs) && episodesRefs.length > 0) {
      // episodesRefs 是 DocumentReference[]，直接使用
      const { getVideosByReferences } = await import("./video");
      videos = await getVideosByReferences(episodesRefs);
      
      // 按 order 排序
      videos.sort((a, b) => (a.order || 0) - (b.order || 0));
    } else {
      // 備用：通過 drama_id 查詢
      const { getVideosByDramaId } = await import("./video");
      videos = await getVideosByDramaId(drama.drama_id);
    }

    return { drama, videos };
  } catch (error) {
    console.error("Error getting drama with episodes:", error);
    throw error;
  }
}

/**
 * 將影集添加到劇集的 episodes 陣列
 */
export async function addEpisodeToDrama(
  dramaId: string,
  videoId: string
): Promise<void> {
  try {
    const dramaRef = doc(db, "dramas", dramaId);
    const dramaSnap = await getDoc(dramaRef);

    if (!dramaSnap.exists()) {
      throw new Error("Drama not found");
    }

    const dramaData = dramaSnap.data();
    const currentEpisodes = dramaData.episodes || [];
    
    // 檢查是否已經存在
    const videoRef = doc(db, "videos", videoId);
    const videoExists = currentEpisodes.some(
      (ref: DocumentReference) => ref.id === videoId
    );

    if (!videoExists) {
      // 添加到陣列
      const updatedEpisodes = [...currentEpisodes, videoRef];
      
      // 更新 drama_length
      const dramaLength = updatedEpisodes.length;
      
      await updateDoc(dramaRef, {
        episodes: updatedEpisodes,
        drama_length: dramaLength,
        updated_at: Timestamp.now(),
      });
    }
  } catch (error) {
    console.error("Error adding episode to drama:", error);
    throw error;
  }
}

/**
 * 從劇集的 episodes 陣列中移除影集
 */
export async function removeEpisodeFromDrama(
  dramaId: string,
  videoId: string,
  deleteVideo: boolean = false
): Promise<void> {
  try {
    const dramaRef = doc(db, "dramas", dramaId);
    const dramaSnap = await getDoc(dramaRef);

    if (!dramaSnap.exists()) {
      throw new Error("Drama not found");
    }

    const dramaData = dramaSnap.data();
    const currentEpisodes = dramaData.episodes || [];
    
    // 從陣列中移除
    const updatedEpisodes = currentEpisodes.filter(
      (ref: DocumentReference) => ref.id !== videoId
    );
    
    // 更新 drama_length
    const dramaLength = updatedEpisodes.length;
    
    await updateDoc(dramaRef, {
      episodes: updatedEpisodes,
      drama_length: dramaLength,
      updated_at: Timestamp.now(),
    });

    // 可選：刪除 video 文檔
    if (deleteVideo) {
      const { deleteVideo: deleteVideoFn } = await import("./video");
      await deleteVideoFn(videoId);
    }
  } catch (error) {
    console.error("Error removing episode from drama:", error);
    throw error;
  }
}

/**
 * 更新劇集的 episodes 順序
 */
export async function updateDramaEpisodesOrder(
  dramaId: string,
  videoIds: string[]
): Promise<void> {
  try {
    const dramaRef = doc(db, "dramas", dramaId);
    const videoRefs = videoIds.map((videoId) => doc(db, "videos", videoId));
    
    await updateDoc(dramaRef, {
      episodes: videoRefs,
      updated_at: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error updating drama episodes order:", error);
    throw error;
  }
}

/**
 * 獲取劇集的總收藏數
 * 從 drama 文檔的 favorites_count 欄位讀取（由 Firestore Trigger 自動維護）
 */
export async function getDramaFavoritesCount(dramaId: string): Promise<number> {
  try {
    const dramaRef = doc(db, "dramas", dramaId);
    const dramaSnap = await getDoc(dramaRef);

    if (!dramaSnap.exists()) {
      return 0;
    }

    const data = dramaSnap.data();
    return data.favorites_count || 0;
  } catch (error) {
    console.error("Error getting drama favorites count:", error);
    return 0;
  }
}

/**
 * 獲取劇集的總喜愛數
 * 聚合所有該劇集的 videos 的 like 欄位
 */
export async function getDramaTotalLikes(dramaId: string): Promise<number> {
  try {
    const { getVideosByDramaId } = await import("./video");
    const videos = await getVideosByDramaId(dramaId);
    
    return videos.reduce((sum, video) => {
      const like = video.like || 0;
      return sum + like;
    }, 0);
  } catch (error) {
    console.error("Error getting drama total likes:", error);
    return 0;
  }
}

